import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Upload,
  X,
  Loader2,
  FileSpreadsheet,
  AlertTriangle,
  Check,
  ChevronRight,
  ChevronLeft,
  Download,
} from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../../api/axios.js';
import { API_ENDPOINTS } from '../../../api/endpoints.js';
import { resolve403ErrorMessage } from '../../../request/getApiErrorMessage.ts';
import { Tooltip } from '../../../admin/components/shared/Tooltip.tsx';
import { useAuditYear } from '../../../contexts/AuditYearContext.tsx';
import { MODULE_KEYS } from '../../../constants/moduleAccess.ts';
import {
  formatModuleAccessDeniedCopy,
  getMissingModuleKeys,
} from '../../../hooks/useRequireModuleAccess.ts';
import { useOrgModuleAccess } from '../../../hooks/useOrgModuleAccess.ts';
import ModuleAccessDeniedDialog from '../../shared/ModuleAccessDeniedDialog.tsx';
import {
  GENERIC_RCM_FIELDS,
  suggestGenericMapping,
  isGenericMappingComplete,
} from './genericRcmFields.js';
import {
  getGenericHeaderCells,
  getGenericRcmAcceptedExtLabel,
  isAcceptedGenericRcmFile,
  readGenericRcmWorkbook,
  suggestGenericHeaderRowIndex,
} from './genericRcmWorkbook.js';
import { buildGenericRcmFileData } from './genericRcmBuildRows.js';
import { importGenericRcmMappedRows } from './genericRcmImport.api.js';

const STEPS = [
  { id: 'upload', label: 'Upload' },
  { id: 'map', label: 'Map' },
  { id: 'preview', label: 'Preview' },
];

function Clamp({ text, max = 200 }) {
  const v = text == null || text === '' ? '—' : String(text);
  return (
    <span
      className="block overflow-hidden text-ellipsis whitespace-nowrap"
      style={{ maxWidth: max }}
      title={v === '—' ? undefined : v}
    >
      {v}
    </span>
  );
}

/** Display value for one mapped platform field on a preview row. */
function previewFieldDisplay(row, fieldKey) {
  switch (fieldKey) {
    case 'classification':
      return row.classification || '';
    case 'entity_name':
      return row.entityName || '';
    case 'cycle_or_domain': {
      const code = row.cycleOrDomainCode || '';
      const name = row.cycleOrDomainName || '';
      return [code, name].filter(Boolean).join(' ');
    }
    case 'process_name':
      return row.processName || '';
    case 'client_control_id':
      return row.clientControlId || '';
    case 'control_title':
      return row.controlTitle || '';
    case 'control_description':
      return row.controlDescription || '';
    case 'control_objective':
      return row.controlObjective || '';
    case 'control_owner':
      return row.controlOwner || '';
    case 'key_reports':
      return Array.isArray(row.keyReports) ? row.keyReports.join(', ') : '';
    case 'frequency':
      return row.frequency || '';
    case 'nature':
      return row.nature || '';
    case 'control_type':
      return row.controlType || '';
    case 'risk_level':
      return row.riskLevel || '';
    case 'risks':
      return Array.isArray(row.risks) ? row.risks.join(' ;; ') : '';
    case 'assertions':
      return Array.isArray(row.assertions) ? row.assertions.join(' ;; ') : '';
    case 'significance':
      return row.significance || '';
    case 'mrc_control':
      return row.mrcControl ? 'true' : 'false';
    default:
      return '';
  }
}

function Stepper({ stepId }) {
  const activeIdx = STEPS.findIndex((s) => s.id === stepId);
  return (
    <ol className="flex flex-wrap items-center gap-1 sm:gap-2">
      {STEPS.map((step, idx) => {
        const done = idx < activeIdx;
        const active = idx === activeIdx;
        return (
          <li key={step.id} className="flex items-center gap-1 sm:gap-2">
            {idx > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" aria-hidden />}
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                active
                  ? 'bg-teal-600 text-white'
                  : done
                    ? 'bg-teal-50 text-teal-800'
                    : 'bg-slate-100 text-slate-500'
              }`}
            >
              <span className="tabular-nums">{idx + 1}</span>
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export default function GenericImportRcmModal({ open, onClose, onImported }) {
  const { selectedAuditYearId, selectedAuditYear } = useAuditYear();
  const isLocked = Boolean(selectedAuditYear?.is_locked);
  const { data: moduleAccess } = useOrgModuleAccess();
  const [accessDeniedOpen, setAccessDeniedOpen] = useState(false);
  const [accessDeniedCopy, setAccessDeniedCopy] = useState({ title: '', message: '' });

  const [step, setStep] = useState('upload');
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState('');
  const [workbook, setWorkbook] = useState(null);
  const [sheetName, setSheetName] = useState('');
  const [headerRowIndex, setHeaderRowIndex] = useState(0);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const sheetRows = workbook?.sheets?.[sheetName] || [];
  const headerCells = useMemo(
    () => getGenericHeaderCells(sheetRows, headerRowIndex),
    [sheetRows, headerRowIndex]
  );

  const usedColumnIndexes = useMemo(() => {
    const used = new Set();
    Object.values(mapping || {}).forEach((idx) => {
      if (idx !== undefined && idx !== null && idx !== '') used.add(Number(idx));
    });
    return used;
  }, [mapping]);

  const mappingComplete = isGenericMappingComplete(mapping);

  const reset = useCallback(() => {
    setStep('upload');
    setBusy(false);
    setFileName('');
    setWorkbook(null);
    setSheetName('');
    setHeaderRowIndex(0);
    setMapping({});
    setPreview(null);
    setIsDragging(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    if (busy) return;
    reset();
    onClose?.();
  }, [busy, reset, onClose]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.getSampleExcelTemplate('rcm'), {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'rcm_upload_sample.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (err) {
      console.error('rcm template download failed', err);
      toast.error(err?.response?.data?.error || 'Failed to download template');
    }
  }, []);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!isAcceptedGenericRcmFile(file)) {
      toast.error(`Use ${getGenericRcmAcceptedExtLabel()}`);
      return;
    }
    setBusy(true);
    setFileName(file.name);
    try {
      const book = await readGenericRcmWorkbook(file);
      const firstSheet = book.sheetNames[0];
      const rows = book.sheets[firstSheet] || [];
      const headerIdx = suggestGenericHeaderRowIndex(rows);
      const headers = getGenericHeaderCells(rows, headerIdx);
      setWorkbook(book);
      setSheetName(firstSheet);
      setHeaderRowIndex(headerIdx);
      setMapping(suggestGenericMapping(headers));
      setPreview(null);
      setStep('map');
    } catch (err) {
      console.error('generic rcm read failed', err);
      toast.error(err?.userFacing ? err.message : err?.message || 'Failed to read file');
      setWorkbook(null);
      setStep('upload');
    } finally {
      setBusy(false);
    }
  }, []);

  const setFieldMapping = useCallback((fieldKey, columnIndexOrEmpty) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (columnIndexOrEmpty === '' || columnIndexOrEmpty === null || columnIndexOrEmpty === undefined) {
        delete next[fieldKey];
        return next;
      }
      const col = Number(columnIndexOrEmpty);
      Object.keys(next).forEach((key) => {
        if (key !== fieldKey && Number(next[key]) === col) {
          delete next[key];
        }
      });
      next[fieldKey] = col;
      return next;
    });
    setPreview(null);
  }, []);

  const handleSheetChange = useCallback(
    (nextSheetName) => {
      if (!workbook || !nextSheetName || nextSheetName === sheetName) return;
      const rows = workbook.sheets?.[nextSheetName] || [];
      const headerIdx = suggestGenericHeaderRowIndex(rows);
      const headers = getGenericHeaderCells(rows, headerIdx);
      setSheetName(nextSheetName);
      setHeaderRowIndex(headerIdx);
      setMapping(suggestGenericMapping(headers));
      setPreview(null);
    },
    [workbook, sheetName]
  );

  const handleHeaderRowChange = useCallback(
    (rawIndex) => {
      const next = Number(rawIndex);
      if (!Number.isFinite(next) || next < 0 || next === headerRowIndex) return;
      const headers = getGenericHeaderCells(sheetRows, next);
      setHeaderRowIndex(next);
      setMapping(suggestGenericMapping(headers));
      setPreview(null);
    },
    [sheetRows, headerRowIndex]
  );

  const goToPreview = useCallback(() => {
    if (!mappingComplete) {
      toast.error('Map Control UID and Risks before preview');
      return;
    }
    try {
      const built = buildGenericRcmFileData(sheetRows, headerRowIndex, mapping);
      if (built.totalRows === 0) {
        toast.error('No data rows with Control UID found');
        return;
      }
      setPreview(built);
      setStep('preview');
    } catch (err) {
      toast.error(err?.userFacing ? err.message : err?.message || 'Failed to build preview');
    }
  }, [mappingComplete, sheetRows, headerRowIndex, mapping]);

  const checkAccess = useCallback(
    (built) => {
      const required = [MODULE_KEYS.RISKS, MODULE_KEYS.CONTROLS];
      if ((built?.businessRows ?? 0) > 0) required.push(MODULE_KEYS.PROCESSES);
      if ((built?.itgcRows ?? 0) > 0) required.push(MODULE_KEYS.SYSTEMS);
      const missing = getMissingModuleKeys(moduleAccess, required);
      if (missing.length === 0) return true;
      setAccessDeniedCopy(
        formatModuleAccessDeniedCopy(
          missing,
          'Importing an RCM file creates records in the modules you are missing access to (risks, controls, processes for business rows, and systems for ITGC rows). Contact your organization administrator if you need access.'
        )
      );
      setAccessDeniedOpen(true);
      return false;
    },
    [moduleAccess]
  );

  const handleConfirmImport = useCallback(async () => {
    if (!preview?.fileData?.length) {
      toast.error('Nothing to import');
      return;
    }
    if (!checkAccess(preview)) return;
    setBusy(true);
    try {
      const result = await importGenericRcmMappedRows({
        fileData: preview.fileData,
        auditYearId: selectedAuditYearId || undefined,
      });
      toast.success(
        `Imported ${result.importedControls} controls and ${result.importedRisks} risks` +
          (result.skippedRows > 0 ? ` (${result.skippedRows} skipped)` : '')
      );
      onImported?.(result);
      reset();
      onClose?.();
    } catch (err) {
      console.error('generic rcm import failed', err);
      toast.error(resolve403ErrorMessage(err, err?.response?.data?.error || 'Failed to import RCM file'));
    } finally {
      setBusy(false);
    }
  }, [preview, checkAccess, selectedAuditYearId, onImported, reset, onClose]);

  if (!open) return null;

  const sheetNames = Array.isArray(workbook?.sheetNames) ? workbook.sheetNames : [];
  const headerOptions = headerCells.map((label, idx) => ({
    idx,
    label: String(label || '').trim() || `(Column ${idx + 1})`,
  }));
  const headerRowPickLimit = Math.min(sheetRows.length, 100);
  const headerRowOptions = Array.from({ length: headerRowPickLimit }, (_, idx) => {
    const cells = (sheetRows[idx] || [])
      .map((c) => String(c ?? '').trim())
      .filter(Boolean);
    const preview = cells.slice(0, 4).join(' · ');
    const label = preview ? `Row ${idx + 1}: ${preview}` : `Row ${idx + 1}`;
    return {
      idx,
      label: label.length > 90 ? `${label.slice(0, 87)}…` : label,
    };
  });

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[1px]">
        <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
          <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-gradient-to-r from-teal-50/80 to-white px-5 py-4">
            <div className="min-w-0 space-y-2">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-teal-700" aria-hidden />
                <h2 className="text-lg font-semibold text-slate-900">Import RCM — Map columns</h2>
              </div>
              <p className="text-xs text-slate-600">
                Upload an RCM file, map its columns to the required fields, then import.
                Extra columns are ignored.
              </p>
              <Stepper stepId={busy && step === 'preview' ? 'preview' : step} />
            </div>
            <button
              type="button"
              onClick={handleClose}
              disabled={busy}
              className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
            {step === 'upload' && (
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  const file = e.dataTransfer?.files?.[0];
                  if (file) void handleFile(file);
                }}
                className={`flex flex-col items-center gap-3 rounded-xl border-2 border-dashed py-14 transition-colors ${
                  isDragging ? 'border-teal-500 bg-teal-50' : 'border-slate-200 bg-slate-50/50'
                }`}
              >
                {busy ? (
                  <>
                    <Loader2 className="h-9 w-9 animate-spin text-teal-600" />
                    <p className="text-sm text-slate-700">Reading {fileName || 'file'}…</p>
                  </>
                ) : (
                  <>
                    <Upload className={`h-10 w-10 ${isDragging ? 'text-teal-600' : 'text-slate-400'}`} />
                    <p className="text-sm font-medium text-slate-800">Drop your RCM file here</p>
                    <p className="max-w-md text-center text-xs text-slate-500">
                      Supports {getGenericRcmAcceptedExtLabel()}. Map columns in the next steps.
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void handleFile(file);
                      }}
                    />
                    <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={handleDownloadTemplate}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        <Download className="h-4 w-4" /> Download template
                      </button>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isLocked || busy}
                        className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Upload className="h-4 w-4" /> Choose file
                      </button>
                    </div>
                    {isLocked && (
                      <p className="text-xs text-amber-700">Audit year is locked — import is disabled.</p>
                    )}
                  </>
                )}
              </div>
            )}

            {step === 'map' && (
              <div className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block space-y-1">
                    <span className="text-xs font-medium text-slate-600">Sheet</span>
                    <select
                      value={sheetName}
                      onChange={(e) => handleSheetChange(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm"
                    >
                      {sheetNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block space-y-1">
                    <span className="text-xs font-medium text-slate-600">Header row</span>
                    <select
                      value={String(headerRowIndex)}
                      onChange={(e) => handleHeaderRowChange(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm"
                    >
                      {headerRowOptions.map((opt) => (
                        <option key={opt.idx} value={opt.idx}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="rounded-lg border border-teal-100 bg-teal-50/70 px-3 py-2 text-xs text-teal-900">
                  Map each required field to at most one column. A column can only map to one field.
                  Unmapped optional fields stay empty. Suggestions are pre-filled — confirm before continuing.
                </div>
                {!mappingComplete && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    Control UID and Risks are required before preview.
                  </div>
                )}
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Platform column</th>
                        <th className="px-3 py-2 font-semibold">Uploaded excel column</th>
                      </tr>
                    </thead>
                    <tbody>
                      {GENERIC_RCM_FIELDS.map((field) => {
                        const selected = mapping[field.key];
                        return (
                          <tr key={field.key} className="border-t border-slate-100">
                            <td className="px-3 py-2 align-top">
                              <div className="font-medium text-slate-900">{field.label}</div>
                              {field.hint ? (
                                <div className="mt-0.5 text-xs text-slate-500">({field.hint})</div>
                              ) : null}
                              <div className="mt-0.5 flex flex-wrap gap-1">
                                {field.required && (
                                  <span className="rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700">
                                    Required
                                  </span>
                                )}
                                {field.recommended && !field.required && (
                                  <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                                    Recommended
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <select
                                value={selected === undefined || selected === null ? '' : String(selected)}
                                onChange={(e) => setFieldMapping(field.key, e.target.value)}
                                className="w-full min-w-[12rem] rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm"
                              >
                                <option value="">— Unmapped —</option>
                                {headerOptions.map((opt) => {
                                  const taken =
                                    usedColumnIndexes.has(opt.idx) && Number(selected) !== opt.idx;
                                  return (
                                    <option key={opt.idx} value={opt.idx} disabled={taken}>
                                      {opt.label}
                                      {taken ? ' (in use)' : ''}
                                    </option>
                                  );
                                })}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {step === 'preview' && preview && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[11px] text-slate-500">Rows</div>
                    <div className="text-lg font-semibold text-slate-900">{preview.totalRows}</div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[11px] text-slate-500">With risks</div>
                    <div className="text-lg font-semibold text-slate-900">{preview.rowsWithRisks}</div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[11px] text-slate-500">Business / ITGC</div>
                    <div className="text-lg font-semibold text-slate-900">
                      {preview.businessRows} / {preview.itgcRows}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[11px] text-slate-500">Warnings</div>
                    <div className="text-lg font-semibold text-slate-900">{preview.warningRows}</div>
                  </div>
                </div>
                {(preview.fileWarnings || []).length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    {preview.fileWarnings.join(' ')}
                  </div>
                )}
                <div className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-900">
                  Scroll the table to check each column. If any values look wrong for a mapped
                  field, use <span className="font-semibold">Back</span> to fix the mapping, then
                  preview again before importing.
                </div>
                <div className="rounded-xl border border-slate-200">
                  {/* Fixed-height viewport so the horizontal scrollbar stays visible
                      at the bottom of the preview (not only after scrolling down). */}
                  <div className="max-h-[min(360px,45vh)] overflow-auto">
                    <table className="min-w-max w-full text-xs">
                      <thead className="sticky top-0 z-20 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="sticky left-0 z-30 bg-slate-50 px-2 py-2 whitespace-nowrap">#</th>
                          <th className="px-2 py-2 whitespace-nowrap">Type</th>
                          {GENERIC_RCM_FIELDS.map((field) => (
                            <th key={field.key} className="px-2 py-2 whitespace-nowrap">
                              {field.label}
                            </th>
                          ))}
                          <th className="px-2 py-2 whitespace-nowrap">Warnings</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.preview.slice(0, 40).map((row, idx) => (
                          <tr key={`${row.rowNumber}-${idx}`} className="border-t border-slate-100">
                            <td className="sticky left-0 z-10 bg-white px-2 py-1.5 text-slate-500">
                              {idx + 1}
                            </td>
                            <td className="px-2 py-1.5">
                              <span
                                className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                                  row.isItgc ? 'bg-purple-50 text-purple-700' : 'bg-teal-50 text-teal-700'
                                }`}
                              >
                                {row.isItgc ? 'ITGC' : 'Business'}
                              </span>
                            </td>
                            {GENERIC_RCM_FIELDS.map((field) => (
                              <td
                                key={field.key}
                                className={`px-2 py-1.5 ${
                                  field.key === 'client_control_id' ? 'font-mono text-[11px]' : ''
                                }`}
                              >
                                <Clamp text={previewFieldDisplay(row, field.key)} max={220} />
                              </td>
                            ))}
                            <td className="px-2 py-1.5 text-amber-800">
                              {(row.warnings || []).length > 0 ? (
                                <Tooltip content={(row.warnings || []).join('; ')} side="left">
                                  <span className="inline-flex items-center gap-1">
                                    <AlertTriangle className="h-3.5 w-3.5" />
                                    {(row.warnings || []).length}
                                  </span>
                                </Tooltip>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {preview.preview.length > 40 && (
                    <div className="border-t border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                      Showing first 40 of {preview.preview.length} rows.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 px-5 py-3">
            <div className="text-xs text-slate-500">
              {step !== 'upload' && fileName ? fileName : ''}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={busy}
                className="rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >
                Cancel
              </button>
              {step === 'map' && (
                <>
                  <button
                    type="button"
                    onClick={() => setStep('upload')}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <ChevronLeft className="h-4 w-4" /> Back
                  </button>
                  <button
                    type="button"
                    onClick={goToPreview}
                    disabled={!mappingComplete}
                    className="inline-flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Preview <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              )}
              {step === 'preview' && (
                <>
                  <button
                    type="button"
                    onClick={() => setStep('map')}
                    disabled={busy}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <ChevronLeft className="h-4 w-4" /> Back
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleConfirmImport()}
                    disabled={isLocked || busy || !preview?.totalRows}
                    className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    Import {preview?.totalRows ?? 0} rows
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <ModuleAccessDeniedDialog
        open={accessDeniedOpen}
        onClose={() => setAccessDeniedOpen(false)}
        title={accessDeniedCopy.title}
        message={accessDeniedCopy.message}
      />
    </>
  );
}
